import { Injectable } from "@angular/core";
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { ServiceResponse } from "@common/data-models/service/service-response";
import { PublicationChannelRecord } from "@common/data-models/records/publication-channel-record";
import { PublicationChannelsResults } from "@common/data-models/publication-channels/publication-channels-results";
import { ServiceRecord } from "@common/data-models/service/service-record";
import { DataStore } from "@common/data-models/data-store/data-store";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";

import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { LanguageSettings } from "@common/services/language-settings";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { BasicContentService } from "@common/services/basic-content-service";
import { ObjectTypes } from "@common/enums/object-types";

@Injectable({ providedIn: 'root' })
export class PublicationChannelsService {

    constructor(
        private nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public basicContentService: BasicContentService
    ) {
    }

    public async createPublicationChannelRecord(ch: PublicationChannelRecord): Promise<ServiceResponse<PublicationChannelRecord>> {
        ch.owner = this.nomWebServicesJson.UserId;
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(true);
        let data = this.nomWebServicesJson.Convertor.serialize(ch);
        delete data["guid"]
        delete data["Guid"]
        return this.basicContentService.createBasicContent<PublicationChannelRecord>(database, path, false, data, null);
    }

    public async updatePublicationChannelRecord(ch: PublicationChannelRecord): Promise<ServiceResponse<PublicationChannelRecord>> {
        ch.owner = this.nomWebServicesJson.UserId;
        let database = this.nomWebServicesJson.getDatabase();
        let path = this.nomWebServicesJson.getPath(true);
        let guid = ch.guid;
        let data = this.nomWebServicesJson.Convertor.serialize(ch, true);
        return this.basicContentService.updateBasicContent<PublicationChannelRecord>(database, path, guid, false, data, false, null);
    }

    public async getPublicationChannelRecord(guid: string): Promise<ServiceResponse<PublicationChannelRecord>> {
        let zFields: Array<{ FieldName: string, FieldValue: any }> = [
            { FieldName: "guid", FieldValue: guid },
            { FieldName: "type", FieldValue: ObjectTypes.PUBLICATION_CHANNEL }
        ];

        let params = this.getBaseParams();
        params.zFields = zFields;
        params["FieldsToExclude"] = ["PSTViewStateScreenshot", "backfields"];

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post(
                "/api/Search",
                params,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load publication channels!",
                detailMessage: `Failed to load publication channels! \n\rStackTrace: PublicationChannelsService.getPublicationChannelRecords(); \n\rError: ${error}`
            });
            return new ServiceResponse<PublicationChannelRecord>(false, null);
        }

        if (response) {
            let responseJSON = response.body;
            if (responseJSON.SearchResults && responseJSON.SearchResults.length > 0) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(responseJSON.SearchResults[0]);
                let channelRecord: PublicationChannelRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<PublicationChannelRecord>(serviceRecord);
                DataStore.set(channelRecord);
                return new ServiceResponse<PublicationChannelRecord>(true, channelRecord);
            }
        }
        return new ServiceResponse<PublicationChannelRecord>(false, null);
    }

    public async getPublicationChannelRecords(): Promise<ServiceResponse<PublicationChannelsResults>> {
        let results: PublicationChannelsResults = new PublicationChannelsResults();

        let zFields: Array<{ FieldName: string, FieldValue: any }> = [
            { FieldName: "virtualPath", FieldValue: "\\\\" + this.nomWebServicesJson.PSTServer },
            { FieldName: "type", FieldValue: ObjectTypes.PUBLICATION_CHANNEL }
        ];

        let params = this.getBaseParams();
        params["zFields"] = zFields;

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post(
                "/api/Search",
                params,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load publication channels!",
                detailMessage: `Failed to load publication channels! \n\rStackTrace: PublicationChannelsService.getPublicationChannelRecords(); \n\rError: ${error}`
            });
            return new ServiceResponse<PublicationChannelsResults>(true, results);
        }

        if (response) {
            for (let p of response.body?.SearchResults) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                try {
                    let channelRecord: PublicationChannelRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<PublicationChannelRecord>(serviceRecord);
                    DataStore.set(channelRecord);
                    results.add(channelRecord);
                } catch (e) {
                    console.log(serviceRecord);
                    console.log(e);
                }
            }
        }
        let res = new ServiceResponse<PublicationChannelsResults>(true, results);
        if (res.status) {
            DataStore.publicationChannelsResults = res.data;
        }
        return res;
    }

    protected getBaseParams(): any {

        let params = {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: "",
            zFields: null,
            NOMLocations: [{
                BasePath: "P_" + this.nomWebServicesJson.PSTGroup + "\\",
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false
            }],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: 300,
            SortByRank: false,
            ExactMatch: false,
            FieldsToExclude: ["backfields"],
            FieldsToReturn: null
        };

        return params;
    }
}
